import { ApiError, api, errorText } from "./api";
import { button, clear, el } from "./dom";
import { closeSheet, openSheet, toast } from "./sheet";
import type { Me } from "./types";

let me: Me | null = null;
const listeners: Array<(me: Me | null) => void> = [];

export function currentMe(): Me | null {
  return me;
}

export function onMeChange(fn: (me: Me | null) => void): void {
  listeners.push(fn);
  fn(me);
}

function setMe(next: Me | null): void {
  me = next;
  for (const fn of listeners) fn(me);
}

/** Resolve the device cookie into a session at startup. Missing cookie is not an error. */
export async function loadMe(): Promise<Me | null> {
  try {
    setMe(await api.me());
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) setMe(null);
    else if (err instanceof ApiError && err.code === "banned") setMe(null);
    else throw err;
  }
  return me;
}

/**
 * Returns the current session, asking for a nickname first if there is none.
 * Resolves to null when the user closes the dialog.
 */
export async function ensureSession(reason: string): Promise<Me | null> {
  if (me) return me;
  return askNickname({
    title: "Hogy hívjunk?",
    hint: reason,
    submitLabel: "Mehet",
    initial: "",
    save: (nickname) => api.createSession(nickname),
  });
}

/** Profile sheet: nickname change plus the device's counters. */
export function openProfileSheet(): void {
  if (!me) {
    void ensureSession("Adj meg egy becenevet, hogy értékelni tudj.");
    return;
  }
  void askNickname({
    title: "Becenevem",
    hint: `Eddig ${me.places_created} helyet adtál hozzá és ${me.ratings_count} értékelést írtál.`,
    submitLabel: "Mentés",
    initial: me.nickname,
    save: (nickname) => api.setNickname(nickname),
  });
}

interface NicknameDialog {
  title: string;
  hint: string;
  submitLabel: string;
  initial: string;
  save: (nickname: string) => Promise<Me>;
}

function askNickname(opts: NicknameDialog): Promise<Me | null> {
  return new Promise((resolve) => {
    let saved: Me | null = null;
    const sheet = openSheet(opts.title, () => resolve(saved));

    const input = el("input", {
      class: "input",
      attrs: {
        type: "text",
        name: "nickname",
        maxlength: "24",
        minlength: "2",
        required: "",
        autocomplete: "nickname",
        placeholder: "pl. Krumplivadász",
      },
    });
    input.value = opts.initial;

    const error = el("p", { class: "form__error", attrs: { role: "alert" } });
    const submit = el("button", { class: "btn btn--primary", type: "submit", text: opts.submitLabel });

    const form = el("form", { class: "form" }, [
      el("p", { class: "form__hint", text: opts.hint }),
      el("label", { class: "form__label", text: "Becenév" }),
      input,
      el("p", { class: "form__note", text: "2–24 karakter. Ez látszik az értékeléseidnél." }),
      error,
      el("div", { class: "form__actions" }, [
        button("Mégsem", "btn", () => closeSheet()),
        submit,
      ]),
    ]);

    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      const nickname = input.value.trim();
      if (nickname.length < 2 || nickname.length > 24) {
        error.textContent = "A becenév 2–24 karakter legyen.";
        return;
      }
      error.textContent = "";
      submit.disabled = true;
      opts
        .save(nickname)
        .then((next) => {
          saved = next;
          setMe(next);
          toast(`Szia, ${next.nickname}!`);
          closeSheet();
        })
        .catch((err: unknown) => {
          error.textContent = errorText(err);
          submit.disabled = false;
        });
    });

    clear(sheet.body);
    sheet.body.append(form);
    input.focus();
  });
}
